import React, { useCallback, useMemo } from "react";
import { DialogHeader, DialogBody } from "components/common/dialogs/GenericDialog";
import { useLocale } from "lib/hooks/useLocale";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { ItemType } from "lib/types/checkout";
import { ItemCard } from "../ItemCard";
import { X } from "@phosphor-icons/react";
import CheckoutItems from "../CheckoutItems";

interface RemoveItemDialogProps {
  setIsOpen: () => void;
  onConfirm: (isLastItem: boolean) => void;
  isLoading?: boolean;
  itemProps: ItemType;
}

const RemoveItemDialog: React.FC<RemoveItemDialogProps> = React.memo(
  ({ setIsOpen, onConfirm, itemProps, isLoading=false }) => {
    const { t } = useLocale();

    const {
      state: { checkoutItems },
    } = useCheckoutContext();

    const { isLastItem, items } = useMemo(() => {
      const currentItem = checkoutItems.filter((item) => item.item_id === itemProps.item_id);

      const otherItems = checkoutItems.filter(
        (item) => item.item_id !== itemProps.item_id && !item.is_freebie,
      );

      // If only upsell items are left, then remove everything
      const isLastItem = otherItems.every((item) => item.is_upsell);
      const items = isLastItem ? [...currentItem, ...otherItems] : currentItem;

      return { isLastItem, items };
    }, [checkoutItems, itemProps.item_id]);

    return (
      <>
        <DialogHeader>
          <div className="flex h-full w-full flex-row items-center justify-between">
            <h1 className="text-base font-medium text-carbon-dark">
              {t("remove_items", {
                item_count: items?.length === 1 ? "1 item" : `${items.length} items`,
              })}
            </h1>
            <button className="outline-none">
              <X className="h-5 w-5 cursor-pointer text-coal-dark" onClick={() => setIsOpen()} />
            </button>
          </div>
        </DialogHeader>

        <DialogBody>
          <div className="relative flex w-full flex-col space-y-6 px-6">
            <CheckoutItems items={items} checkoutItemsMutable={false} blockVarientSelector={true} />
            <PrimaryButton
              buttonText={
                Boolean(isLastItem) ? t("remove_last_item_dialog_cta") : t("remove_item_dialog_cta")
              }
              onClick={() => onConfirm(isLastItem)}
              height="h-14"
              isLoading={isLoading}
              isDisabled={false}
            />
          </div>
        </DialogBody>
      </>
    );
  },
);

export default RemoveItemDialog;
